import { useState } from 'react';

import {
  useCreateTemplate,
  useDeleteTemplate,
  useTemplates,
  type TemplateRow,
} from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  ConfirmModal,
  DrawerPanel,
  EmptyState,
  Field,
  FilterChip,
  IconButton,
  Input,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui/index.js';

export function SettingsTemplates(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const templatesQ = useTemplates(slug);
  const createTemplate = useCreateTemplate(slug);
  const [kind, setKind] = useState<'all' | 'chat' | 'form' | 'survey'>('all');
  const list = (templatesQ.data ?? []).filter((t) => kind === 'all' || t.kind === kind);

  const [showCreate, setShowCreate] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplKind, setTplKind] = useState<'chat' | 'form' | 'survey'>('chat');
  const [tplText, setTplText] = useState('');

  async function handleCreate(): Promise<void> {
    if (!tplName.trim()) return;
    await createTemplate.mutateAsync({
      name: tplName.trim(),
      kind: tplKind,
      config: { text: tplText.trim() },
    });
    setShowCreate(false);
    setTplName('');
    setTplText('');
  }

  function openCreate(): void {
    if (kind !== 'all') setTplKind(kind);
    setShowCreate(true);
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Шаблоны"
        badge={{ count: list.length }}
        actions={
          <Button variant="primary" icon="ti-plus" onClick={openCreate}>
            Создать шаблон
          </Button>
        }
        filters={
          <>
            <FilterChip active={kind === 'all'} onClick={() => setKind('all')}>
              Все
            </FilterChip>
            <FilterChip active={kind === 'chat'} onClick={() => setKind('chat')}>
              Чат-сообщения
            </FilterChip>
            <FilterChip active={kind === 'form'} onClick={() => setKind('form')}>
              Формы
            </FilterChip>
            <FilterChip active={kind === 'survey'} onClick={() => setKind('survey')}>
              Опросы
            </FilterChip>
          </>
        }
      />
      <div className="inbx-page__body">
        {list.length === 0 && !templatesQ.isLoading ? (
          <EmptyState
            icon="ti-template"
            title="Шаблонов нет"
            description="Создай первый шаблон быстрого ответа или формы"
          />
        ) : (
          <div className="inbx-grid-2">
            {list.map((t) => (
              <TemplateCard key={t.id} t={t} workspaceSlug={slug} />
            ))}
          </div>
        )}
      </div>

      <DrawerPanel
        open={showCreate}
        title="Новый шаблон"
        onClose={() => setShowCreate(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createTemplate.isPending || !tplName.trim()}
            >
              {createTemplate.isPending ? 'Создание…' : 'Создать'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Название" required>
            <Input
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              placeholder="Приветствие"
              autoFocus
            />
          </Field>
          <Field label="Тип">
            <Select
              value={tplKind}
              onChange={(e) => setTplKind(e.target.value as 'chat' | 'form' | 'survey')}
            >
              <option value="chat">Чат-сообщение</option>
              <option value="form">Форма</option>
              <option value="survey">Опрос</option>
            </Select>
          </Field>
          <Field label="Текст шаблона">
            <Textarea
              value={tplText}
              onChange={(e) => setTplText(e.target.value)}
              placeholder="Здравствуйте! Чем могу помочь?"
              rows={5}
            />
          </Field>
        </div>
      </DrawerPanel>
    </div>
  );
}

function TemplateCard({
  t,
  workspaceSlug,
}: {
  t: TemplateRow;
  workspaceSlug: string | undefined;
}): JSX.Element {
  const deleteTemplate = useDeleteTemplate(workspaceSlug);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const text = (t.config as { text?: string })?.text ?? '';

  return (
    <div className="inbx-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 500 }}>{t.name}</span>
        <span className="inbx-pill-badge inbx-pill-badge--info">{t.kind}</span>
        {t.isSystem && <span className="inbx-pill-badge inbx-pill-badge--default">системный</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          <IconButton icon="ti-edit" tooltip="Редактировать" />
          {!t.isSystem && (
            <IconButton icon="ti-trash" tooltip="Удалить" onClick={() => setConfirmDelete(true)} />
          )}
        </span>
      </div>
      {text && (
        <div
          style={{
            background: 'var(--inbx-bg-secondary)',
            padding: 10,
            borderRadius: 'var(--inbx-radius)',
            fontSize: 'var(--inbx-fz-sm)',
            color: 'var(--inbx-text-secondary)',
          }}
        >
          {text}
        </div>
      )}

      <ConfirmModal
        open={confirmDelete}
        title="Удалить шаблон?"
        description={`Шаблон «${t.name}» будет удалён без возможности восстановления.`}
        confirmLabel="Удалить"
        variant="danger"
        onConfirm={() => {
          deleteTemplate.mutate(t.id);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
